import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Hero from '../components/Hero';
import { Project } from '../types/project';
import { projectService } from '../services/projectService';
import { featureService, Feature } from '../services/featureService';
import { testimonialService, Testimonial } from '../services/testimonialService';
import * as HeroIcons from '@heroicons/react/24/outline';
import { motion, AnimatePresence } from 'framer-motion';

// Dynamic icon rendering based on string name
const DynamicIcon = ({ iconName, className }: { iconName: string, className?: string }) => {
  // @ts-ignore - We're dynamically accessing properties
  const IconComponent = HeroIcons[iconName.split('-').map(part => 
    part.charAt(0).toUpperCase() + part.slice(1)).join('') + 'Icon'];

  if (!IconComponent) {
    return <HeroIcons.StarIcon className={className} />;
  }

  return <IconComponent className={className} />;
};

// Testimonial Card component with expand/collapse functionality
const TestimonialCard: React.FC<{ testimonial: Testimonial }> = ({ testimonial }) => {
  const [expanded, setExpanded] = useState(false);
  const maxLength = 150; // Character limit for collapsed state
  const isLongComment = testimonial.comment.length > maxLength;
  
  return (
    <div className="bg-white p-6 rounded-lg shadow-md h-full flex flex-col">
      <div className="flex mb-4">
        {[...Array(5)].map((_, i) => (
          <svg 
            key={i} 
            className={`w-5 h-5 ${i < testimonial.rating ? 'text-yellow-400' : 'text-gray-300'}`} 
            fill="currentColor" 
            viewBox="0 0 20 20"
          >
            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
          </svg>
        ))}
      </div>
      
      <div className="flex-grow">
        <div className="italic text-primary-600 mb-4">
          <p className={expanded ? "" : "line-clamp-4"}>
            "{testimonial.comment}"
          </p>
          {isLongComment && (
            <button 
              onClick={() => setExpanded(!expanded)}
              className="text-accent-600 hover:text-accent-700 font-medium text-sm mt-2 flex items-center focus:outline-none"
            >
              {expanded ? 'Read Less' : 'Read More'}
              <motion.svg 
                xmlns="http://www.w3.org/2000/svg" 
                className="h-4 w-4 ml-1"
                fill="none" 
                viewBox="0 0 24 24" 
                stroke="currentColor"
                animate={{ rotate: expanded ? 180 : 0 }}
                transition={{ duration: 0.3 }}
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={expanded ? "M5 15l7-7 7 7" : "M19 9l-7 7-7-7"} />
              </motion.svg>
            </button>
          )}
        </div>
      </div>
      
      <div className="flex items-center mt-auto pt-4 border-t border-gray-100">
        <div className="bg-primary-100 w-12 h-12 rounded-full flex items-center justify-center text-primary-700 font-bold mr-3">
          {testimonial.initials}
        </div>
        <div>
          <p className="font-semibold text-primary-900">{testimonial.clientName}</p>
          <p className="text-primary-500 text-sm">{testimonial.projectName}</p>
        </div>
      </div>
    </div>
  );
};

const Home = () => {
  const [featuredProjects, setFeaturedProjects] = useState<Project[]>([]);
  const [features, setFeatures] = useState<Feature[]>([]);
  const [testimonials, setTestimonials] = useState<Testimonial[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [featureLoading, setFeatureLoading] = useState(true);
  const [testimonialLoading, setTestimonialLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [featureError, setFeatureError] = useState<string | null>(null);
  const [testimonialError, setTestimonialError] = useState<string | null>(null);

  useEffect(() => {
    loadFeaturedProjects();
    loadFeatures();
    loadTestimonials();
  }, []);

  const loadFeaturedProjects = async () => {
    try {
      setIsLoading(true);
      const projects = await projectService.getFeaturedProjects();
      setFeaturedProjects(projects);
    } catch (err) {
      console.error('Error loading featured projects:', err);
      setError('Failed to load featured projects');
    } finally {
      setIsLoading(false);
    }
  };

  const loadFeatures = async () => {
    try {
      setFeatureLoading(true);
      const data = await featureService.getActiveFeatures();
      setFeatures(data);
    } catch (err) {
      console.error('Error loading features:', err);
      setFeatureError('Failed to load features');
    } finally {
      setFeatureLoading(false);
    }
  };

  const loadTestimonials = async () => {
    try {
      setTestimonialLoading(true);
      const data = await testimonialService.getActiveTestimonials();
      setTestimonials(data);
    } catch (err) {
      console.error('Error loading testimonials:', err);
      setTestimonialError('Failed to load testimonials');
    } finally {
      setTestimonialLoading(false);
    }
  };

  // Fallback features in case none are in the database yet
  const defaultFeatures = [
    {
      id: 'default1',
      title: 'Quality Craftsmanship',
      description: 'We use only the finest materials and employ skilled craftsmen to ensure every detail meets our high standards.',
      icon: 'star',
      order: 0,
      active: true
    },
    {
      id: 'default2',
      title: 'Personalized Service',
      description: 'We work closely with you to understand your vision and bring it to life, ensuring your complete satisfaction.',
      icon: 'check',
      order: 1,
      active: true
    },
    {
      id: 'default3',
      title: 'Attention to Detail',
      description: 'Every aspect of your home is carefully planned and executed to create a living space that exceeds expectations.',
      icon: 'heart',
      order: 2,
      active: true
    }
  ];

  // Use database features if available, otherwise use defaults
  const displayFeatures = features.length > 0 ? features : defaultFeatures;

  // Default testimonials in case none are in the database yet
  const defaultTestimonials = [
    {
      id: 'default1',
      clientName: 'John & Diana Smith',
      projectName: 'Mountain View Estate',
      comment: 'Ridgeline Homes turned our dream into reality. Their attention to detail and commitment to quality exceeded our expectations.',
      rating: 5,
      initials: 'JD',
      order: 0,
      active: true
    },
    {
      id: 'default2',
      clientName: 'Michael Johnson',
      projectName: 'Lakeside Retreat',
      comment: 'The craftsmanship and professionalism from the Ridgeline team was outstanding. They made the building process stress-free and enjoyable.',
      rating: 5,
      initials: 'MJ',
      order: 1,
      active: true
    },
    {
      id: 'default3',
      clientName: 'Amy & Robert Chen',
      projectName: 'Aspen Heights',
      comment: "We couldn't be happier with our new home. The quality of construction and the thoughtful design touches make it perfect for our family.",
      rating: 5,
      initials: 'AR',
      order: 2,
      active: true
    }
  ];

  // Use database testimonials if available, otherwise use defaults
  const displayTestimonials = testimonials.length > 0 ? testimonials : defaultTestimonials;

  return (
    <div className="bg-white">
      <Hero />

      {/* Featured Projects */}
      <div className="container mx-auto px-4 py-16">
        <h2 className="text-3xl font-display font-bold text-primary-900 mb-8">
          Featured Projects
        </h2>
        
        {isLoading ? (
          <div className="flex justify-center items-center h-64">
            <p className="text-primary-500">Loading featured projects...</p>
          </div>
        ) : error ? (
          <div className="bg-red-50 p-4 rounded-md text-red-700">{error}</div>
        ) : featuredProjects.length === 0 ? (
          <div className="text-center py-10">
            <p className="text-primary-600">No featured projects available at this time.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {featuredProjects.map((project) => (
              <div key={project.id} className="bg-white rounded-lg shadow-lg overflow-hidden">
                {project.images && project.images.length > 0 ? (
                  <img 
                    src={project.images[0]} 
                    alt={project.title} 
                    className="h-64 w-full object-cover"
                  />
                ) : (
                  <div className="h-64 w-full bg-primary-100 flex items-center justify-center">
                    <span className="text-primary-500">No Image Available</span>
                  </div>
                )}
                <div className="p-6">
                  <div className="flex justify-between items-center mb-2">
                    <h3 className="text-xl font-semibold text-primary-900">
                      {project.title}
                    </h3>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      project.status === 'Completed' 
                        ? 'bg-green-100 text-green-800' 
                        : project.status === 'In Progress' 
                        ? 'bg-blue-100 text-blue-800' 
                        : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {project.status}
                    </span>
                  </div>
                  <p className="text-primary-600 mb-2">
                    {project.location} • {project.squareFeet} sq ft
                  </p>
                  {project.description && (
                    <p className="text-primary-500 mb-4 line-clamp-2">
                      {project.description}
                    </p>
                  )}
                  <Link
                    to={`/projects/${project.id}`}
                    className="text-accent-600 hover:text-accent-700 font-medium inline-flex items-center"
                  >
                    View Project
                    <svg className="ml-1 w-4 h-4" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                      <path fillRule="evenodd" d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </Link>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Why Choose Us */}
      <div className="bg-primary-50 py-16">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-display font-bold text-primary-900 mb-12 text-center">
            Why Choose Ridgeline Homes
          </h2>
          
          {featureLoading ? (
            <div className="flex justify-center items-center h-32">
              <p className="text-primary-500">Loading features...</p>
            </div>
          ) : featureError ? (
            <div className="bg-red-50 p-4 rounded-md text-red-700 max-w-md mx-auto">
              {featureError}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {displayFeatures.map((feature) => (
                <div key={feature.id} className="text-center">
                  <div className="w-16 h-16 bg-accent-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <DynamicIcon iconName={feature.icon} className="w-8 h-8 text-accent-600" />
                  </div>
                  <h3 className="text-xl font-semibold text-primary-900 mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-primary-600">
                    {feature.description}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Testimonials Section */}
      <div className="py-16 container mx-auto px-4">
        <h2 className="text-3xl font-display font-bold text-primary-900 mb-12 text-center">
          What Our Clients Say
        </h2>
        
        {testimonialLoading ? (
          <div className="flex justify-center items-center h-32">
            <p className="text-primary-500">Loading testimonials...</p>
          </div>
        ) : testimonialError ? (
          <div className="bg-red-50 p-4 rounded-md text-red-700 max-w-md mx-auto">
            {testimonialError}
          </div>
        ) : displayTestimonials.length === 0 ? (
          <div className="text-center py-10">
            <p className="text-primary-600">No testimonials available at this time.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {displayTestimonials.map((testimonial) => (
              <TestimonialCard key={testimonial.id} testimonial={testimonial} />
            ))}
          </div>
        )}
        
        <div className="text-center mt-10">
          <Link
            to="/contact"
            className="inline-block px-6 py-3 bg-accent-600 text-white font-medium rounded-md hover:bg-accent-700 transition-colors"
          >
            Contact Us Today
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Home; 